// Helpers puros de la sección "Uso de servicios" del 360 (111-plan-template-service-usage).
// Trabajan sobre la respuesta de GET /memberships/360/:membershipId/service-usage: el backend
// entrega números crudos en la moneda del plan y aquí se formatean, filtran y convierten a UF.
import CurrencyUtils from './currency.utils';

const PERCENT = 100;
const UF = 'UF';
const CLP = 'CLP';

// Moneda de facturación del plan → país cuyo formato de dinero se reutiliza.
const CURRENCY_COUNTRY = { CLP: 'CL', PEN: 'PE', MXN: 'MX' };

// null/undefined/0 no son un tope ni un valor UF utilizable.
const isPositive = (value) => Number(value) > 0;

const formatNumber = (value, decimals) =>
  Number(value).toLocaleString('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

export function formatUf(value) {
  return `UF ${formatNumber(value, 2)}`;
}

// Valor de la UF en pesos para el pie de la sección: "$39.120,45".
export function formatUfValue(value) {
  return `$${formatNumber(value, 2)}`;
}

// Formatea un monto expresado en `currency`. `$` con miles para CLP, "UF 15,00" para UF y el
// símbolo del país para PEN/MXN; una moneda desconocida se muestra con su código.
export function formatMoney(value, currency) {
  if (currency === UF) return formatUf(value);
  const country = CURRENCY_COUNTRY[currency];
  if (country) return CurrencyUtils.formatCountryAmount(value, country, country === 'CL' ? '' : ' ');
  return `${currency} ${formatNumber(value, 0)}`;
}

// Monto en la moneda del plan mostrado en la unidad elegida por el toggle. Solo un plan en
// CLP con valor UF disponible se convierte; en cualquier otro caso queda en su moneda.
export function toDisplayAmount(value, { currency, unit, ufValue }) {
  if (unit === UF && currency === CLP && isPositive(ufValue)) return formatUf(value / ufValue);
  return formatMoney(value, currency);
}

// Tope por evento: si el backend no pudo convertir el tope UF (sin valor UF), se muestra el
// valor original en UF en lugar de la conversión.
export function formatPerEvent(maxAmountPerEvent, { currency, unit, ufValue }) {
  if (!maxAmountPerEvent) return '—';
  if (maxAmountPerEvent.currency === UF && (!isPositive(ufValue) || unit === UF)) {
    return formatUf(maxAmountPerEvent.value);
  }
  return toDisplayAmount(maxAmountPerEvent.valueInPlanCurrency, { currency, unit, ufValue });
}

const COVERAGE_MODE_LABELS = {
  cupones: 'Cupones',
  pozo: 'Pozo',
  'cupones-pozo': 'Cupones + pozo',
  'sin-tope': 'Sin tope'
};

export function coverageModeLabel(mode) {
  return COVERAGE_MODE_LABELS[mode] || '—';
}

// El backend reutiliza el texto del refund ("...edad máxima..." / "...edad mínima..."); sin
// razón se asume el caso habitual, la edad máxima.
const BELOW_MIN_AGE = /m[ií]nima/i;
const MONTHS_PER_YEAR = 12;

function ageChipLabel(age) {
  if (BELOW_MIN_AGE.test(age.reason || '')) {
    return `Edad mín. ${age.lowerLimitMonths} meses`;
  }
  return `Edad máx. ${Math.floor(age.upperLimitMonths / MONTHS_PER_YEAR)} años`;
}

// Chip de estado de un servicio. El estado lo resuelve el backend con la prioridad de FR-014.
export function statusChip(item) {
  switch (item.status) {
    case 'AGE_INELIGIBLE':
      return { label: ageChipLabel(item.age), color: 'grey' };
    case 'IN_GRACE_PERIOD':
      return { label: `Carencia · ${item.gracePeriod.daysLeft} días`, color: 'blue' };
    case 'EXHAUSTED':
      return { label: 'Agotado', color: 'red' };
    case 'NEARLY_EXHAUSTED':
      return { label: `${Math.round(item.usageRatio * PERCENT)}% usado`, color: 'orange' };
    default:
      return { label: 'Disponible', color: 'green' };
  }
}

export function formatDeductible(deductible) {
  if (!deductible || !isPositive(deductible.value)) return '—';
  if (deductible.currency === '%') return `${deductible.value}%`;
  return formatMoney(deductible.value, deductible.currency);
}

// Fecha 'YYYY-MM-DD' o ISO → 'DD/MM/YYYY', leída por partes de texto: `new Date('2026-07-20')`
// se interpreta en UTC y en hora de Chile caería un día antes.
export function formatIsoDate(text) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text || '');
  if (!match) return '—';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

// Porcentaje de consumo para la barra de progreso; null cuando no hay tope.
export function usagePercent({ used, total }) {
  if (!isPositive(total)) return null;
  return Math.min(PERCENT, Math.round((used / total) * PERCENT));
}
